import {
  Label,
  NonCopyableMarkdown,
  SectionWrapper,
  MatchingColumnsWrapper,
  MatchingColumn,
  StaticOption,
  NonCopyableMarkdownOption,
} from '../common';
import React from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragMoveEvent,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { useTheme } from 'styled-components';
import { useDndScrollLock } from '../../../hooks/assessment-test/use-dnd-scroll-lock';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';

export default function MatchingSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'matching' }
  >;
  stepId: string;
}) {
  const { answer, content, leftItems, rightItems } = section;
  const [, dispatch] = React.useContext(AssessmentTestContext);
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const leftRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const rightRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [linePositions, setLinePositions] = React.useState<
    Array<{ x1: number; y1: number; x2: number; y2: number }>
  >([]);
  const [dragLine, setDragLine] =
    React.useState<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const setAnswer = React.useCallback(
    (value: Array<{ leftId: string; rightId: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_MATCHING',
        payload: {
          stepId,
          sectionId: section.id,
          answer: value,
        },
      });
    },
    [stepId, section.id, dispatch]
  );

  const { onDragStart, wrapOnDragEnd } = useDndScrollLock();

  const computePositions = React.useCallback(() => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const positions = answer
      .map(({ leftId, rightId }) => {
        const leftEl = leftRefs.current[leftId];
        const rightEl = rightRefs.current[rightId];
        if (!leftEl || !rightEl) return null;
        const l = leftEl.getBoundingClientRect();
        const r = rightEl.getBoundingClientRect();
        const x1 = l.left + l.width - containerRect.left - 0.5;
        const y1 = l.top + l.height / 2 - containerRect.top;
        const x2 = r.left - containerRect.left + 0.5;
        const y2 = r.top + r.height / 2 - containerRect.top;
        return { x1, y1, x2, y2 };
      })
      .filter(Boolean) as Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }>;
    setLinePositions(positions);
  }, [answer]);

  React.useEffect(() => {
    computePositions();
    const onResize = () => computePositions();
    window.addEventListener('resize', onResize);
    const interval = setInterval(computePositions, 100); // recompute while layout stabilizes
    return () => {
      window.removeEventListener('resize', onResize);
      clearInterval(interval);
    };
  }, [computePositions]);

  const onDragStartInternal = React.useCallback((event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);
    if (activeId.startsWith('left:')) {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        // Use the actual pointer position where dragging started
        const x1 =
          (event.activatorEvent as PointerEvent).clientX - containerRect.left;
        const y1 =
          (event.activatorEvent as PointerEvent).clientY - containerRect.top;
        setDragLine({ x1, y1, x2: x1, y2: y1 });
      }
    }
  }, []);

  const onDragMove = React.useCallback(
    (event: DragMoveEvent) => {
      if (dragLine && event.delta) {
        setDragLine((prev) =>
          prev
            ? {
                ...prev,
                x2: prev.x1 + event.delta.x,
                y2: prev.y1 + event.delta.y,
              }
            : null
        );
      }
    },
    [dragLine]
  );

  const onDragEndInternal = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setDragLine(null);

      if (!over) return;
      const activeId = String(active.id);
      const overId = String(over.id);
      if (!activeId.startsWith('left:') || !overId.startsWith('right:')) return;
      const leftId = activeId.replace('left:', '');
      const rightId = overId.replace('right:', '');
      // enforce one-to-one matching: remove any pairs with this leftId or rightId
      const next = answer
        .filter((p) => p.leftId !== leftId && p.rightId !== rightId)
        .concat({ leftId, rightId });
      setAnswer(next);
      // recompute lines next paint
      requestAnimationFrame(computePositions);
    },
    [answer, computePositions, setAnswer]
  );

  const onDragStartCombined = React.useCallback(
    (event: DragStartEvent) => {
      onDragStart();
      onDragStartInternal(event);
    },
    [onDragStart, onDragStartInternal]
  );

  const onDragEnd = React.useMemo(
    () => wrapOnDragEnd<DragEndEvent>(onDragEndInternal),
    [wrapOnDragEnd, onDragEndInternal]
  );

  return (
    <SectionWrapper ref={containerRef} style={{ position: 'relative' }}>
      <NonCopyableMarkdown md={content} />
      <DndContext
        sensors={sensors}
        onDragStart={onDragStartCombined}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        // modifiers={[restrictToFirstScrollableAncestor, restrictToParentElement]}
      >
        <MatchingColumnsWrapper>
          <MatchingColumn>
            {leftItems.map((item) => (
              <LeftDraggable
                key={item.id}
                id={`left:${item.id}`}
                registerRef={(el) => (leftRefs.current[item.id] = el)}
                isMatched={!!answer.find((p) => p.leftId === item.id)}
                content={item.content}
              />
            ))}
          </MatchingColumn>
          <MatchingColumn>
            {rightItems.map((item) => (
              <RightDroppable
                key={item.id}
                id={`right:${item.id}`}
                registerRef={(el) => (rightRefs.current[item.id] = el)}
                isMatched={!!answer.find((p) => p.rightId === item.id)}
                content={item.content}
              />
            ))}
          </MatchingColumn>
        </MatchingColumnsWrapper>
        {/* SVG overlay for connections */}
        <svg
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {linePositions.map((p, idx) => (
            <g key={`line-${idx}`}>
              <line
                x1={p.x1}
                y1={p.y1}
                x2={p.x2}
                y2={p.y2}
                stroke={theme.colors.textPrimary}
                strokeWidth={1}
              />
            </g>
          ))}
          {/* Active drag line */}
          {dragLine && (
            <line
              x1={dragLine.x1}
              y1={dragLine.y1}
              x2={dragLine.x2}
              y2={dragLine.y2}
              stroke={theme.colors.textPrimary}
              strokeWidth={1}
              strokeDasharray="4 4"
              opacity={0.7}
            />
          )}
        </svg>
      </DndContext>
      <Label>Drag a left item onto a right item to create a match.</Label>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}

function LeftDraggable({
  id,
  registerRef,
  isMatched,
  content,
}: {
  id: string;
  registerRef: (el: HTMLDivElement | null) => void;
  isMatched: boolean;
  content: string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
  });
  const style: React.CSSProperties = {
    cursor: 'grab',
  };
  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        registerRef(el);
      }}
      style={style}
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <StaticOption isSelected={isMatched || isDragging}>
        <NonCopyableMarkdownOption md={content} />
      </StaticOption>
    </div>
  );
}

function RightDroppable({
  id,
  registerRef,
  isMatched,
  content,
}: {
  id: string;
  registerRef: (el: HTMLDivElement | null) => void;
  isMatched: boolean;
  content: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        registerRef(el);
      }}
      tabIndex={-1}
    >
      <StaticOption isSelected={isMatched || isOver}>
        {/* <NonCopyableSpan>{content}</NonCopyableSpan> */}
        <NonCopyableMarkdownOption md={content} />
      </StaticOption>
    </div>
  );
}
